import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import {
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Grid,
  ListItem,
  ListItemText,
  Button,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import IconButton from '@mui/material/IconButton';
import LaunchIcon from '@mui/icons-material/Launch';
import GroupsIcon from '@mui/icons-material/Groups';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const industryNames = {
  insurance: 'Insurance',
  healthcare: 'Healthcare',
  education: 'Education',
  'small-industries': 'Small Industries',
  research: 'Research',
  travel: 'Travel',
  'real-estate': 'Real Estate',
  finance: 'Finance',
  banking: 'Banking',
  legal: 'Legal',
  it: 'IT',
};

const IndustryDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const { name } = useParams();
  const readableName = industryNames[name] || 'Industry';
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem('token');
      setLoading(true);
      try {
        const [oppsRes, provRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/opportunities/getOpportunities_industrypresence?industry=${name}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/industries_providers/${name}`),
        ]);
        setOpportunities(oppsRes.data || []);
        setProviders(provRes.data?.providers || []);
      } catch (error) {
        console.error('Error fetching industry details', error);
        setOpportunities([]);
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [name]);

  const stripHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const previewText = (html, words = 18) => {
    const plainText = stripHtml(html || '').trim();
    if (!plainText) return 'No details added yet.';
    const parts = plainText.split(/\s+/);
    return parts.length > words ? `${parts.slice(0, words).join(' ')}...` : plainText;
  };

  const goToOpportunity = (opportunityNumber) => {
    navigate(`/2.0/opportunities?opportunity_number=${opportunityNumber}`);
  };

  const providerPreview = providers.slice(0, isMobile ? 6 : 12);

  const columns = [
    { field: 'opportunity_number', headerName: 'Number', width: 120 },
    { field: 'role', headerName: 'Role', width: 180 },
    { field: 'company', headerName: 'Company', width: 180 },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 280,
      renderCell: (params) => {
        const plainText = stripHtml(params.value || '');
        return (
          <Typography variant="body2" noWrap title={plainText}>
            {previewText(params.value, 5)}
          </Typography>
        );
      },
    },
    { field: 'link', headerName: 'Link', width: 180 },
    { field: 'noapplications', headerName: 'Applications', width: 140 },
    {
      field: 'apply',
      headerName: 'Apply',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => goToOpportunity(params.row.opportunity_number)}
        >
          <LaunchIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        mx: { xs: -2, sm: -3, md: -4 },
        my: { xs: -2, sm: -3, md: -4 },
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 12% 12%, rgba(20, 184, 166, 0.16), transparent 30%), radial-gradient(circle at 88% 4%, rgba(37, 99, 235, 0.13), transparent 28%), linear-gradient(135deg, #f8fafc 0%, #eef7f6 48%, #f8fafc 100%)',
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.45fr 1fr' },
            gap: 2.5,
            alignItems: 'stretch',
            mb: 3,
          }}
        >
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 4 },
                borderRadius: 4,
                border: '1px solid rgba(15, 118, 110, 0.14)',
                boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
                height: '100%',
              }}
            >
              <Chip
                label={`${readableName} Industry`}
                sx={{
                  mb: 2,
                  bgcolor: '#e6fffb',
                  color: '#0f766e',
                  border: '1px solid #99f6e4',
                  fontWeight: 800,
                }}
              />
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{ color: '#0f172a', fontWeight: 900, lineHeight: 1.08, mb: 1.5 }}
              >
                Discover {readableName} support, providers, and opportunities.
              </Typography>
              <Typography sx={{ color: '#475569', maxWidth: 760, lineHeight: 1.7 }}>
                See active community presence in this industry, review live opportunities,
                and raise a focused request when you need guidance from available providers.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2.5 }}>
                <Button
                  component={RouterLink}
                  to="/2.0/request"
                  variant="contained"
                  startIcon={<HelpOutlineIcon />}
                  sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#115e59' }, fontWeight: 900 }}
                >
                  Raise a Request
                </Button>
                <Button
                  component={RouterLink}
                  to="/2.0/opportunities"
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderColor: '#0f766e', color: '#0f766e', fontWeight: 900 }}
                >
                  View All Opportunities
                </Button>
              </Stack>
            </Paper>
          </motion.div>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: '1fr' },
              gap: 1.5,
            }}
          >
            {[
              { label: 'Active providers', value: providers.length },
              { label: 'Open opportunities', value: opportunities.length },
              { label: 'Current industry', value: readableName },
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid rgba(15, 118, 110, 0.12)',
                  bgcolor: 'rgba(255, 255, 255, 0.82)',
                  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ color: '#0f172a', fontWeight: 900, mt: 0.75 }}>
                  {stat.value}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 10 }}>
            <CircularProgress sx={{ color: '#0f766e' }} />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  height: '100%',
                  border: '1px solid rgba(15, 118, 110, 0.13)',
                  boxShadow: '0 22px 70px rgba(15, 23, 42, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GroupsIcon sx={{ color: '#0f766e' }} />
                  <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 900 }}>
                    Active Providers
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                  Community members and associations available across companies in {readableName}.
                </Typography>
                {providers.length === 0 ? (
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      No active providers are listed yet.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: { xs: 'none', lg: 430 }, overflow: 'auto' }}>
                    {providerPreview.map((association, index) => (
                      <ListItem
                        key={`${association}-${index}`}
                        sx={{
                          px: 1.25,
                          py: 1,
                          mb: 1,
                          borderRadius: 2,
                          bgcolor: '#f8fafc',
                          border: '1px solid #eef2f7',
                        }}
                      >
                        <ListItemText
                          primary={`${index + 1}. ${association}`}
                          primaryTypographyProps={{ sx: { color: '#334155', fontWeight: 700 } }}
                        />
                      </ListItem>
                    ))}
                    {providers.length > providerPreview.length && (
                      <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                        +{providers.length - providerPreview.length} more providers
                      </Typography>
                    )}
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Button
                  component={RouterLink}
                  to="/2.0/request"
                  fullWidth
                  variant="outlined"
                  startIcon={<HelpOutlineIcon />}
                  sx={{ borderColor: '#0f766e', color: '#0f766e', fontWeight: 900 }}
                >
                  Ask Providers for Help
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  backgroundColor: '#fff',
                  border: '1px solid rgba(15, 118, 110, 0.13)',
                  boxShadow: '0 22px 70px rgba(15, 23, 42, 0.08)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ display: 'flex', alignItems: 'center', color: '#0f172a', fontWeight: 900 }}
                    >
                      <WorkOutlineIcon sx={{ mr: 1, color: '#0f766e' }} />
                      Latest Opportunities
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                      Apply from this list or open the full opportunities board with the selected opportunity number.
                    </Typography>
                  </Box>
                  <Button
                    component={RouterLink}
                    to="/2.0/opportunities"
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ borderColor: '#0f766e', color: '#0f766e', fontWeight: 900 }}
                  >
                    View All
                  </Button>
                </Box>

                {opportunities.length === 0 ? (
                  <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <Typography sx={{ color: '#0f172a', fontWeight: 900 }}>No opportunities posted yet.</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.75 }}>
                      Check the full opportunities page or raise a request for guidance in {readableName}.
                    </Typography>
                  </Box>
                ) : isMobile ? (
                  <Stack spacing={1.5}>
                    {opportunities.map((opportunity) => (
                      <Paper
                        key={opportunity.id || opportunity.opportunity_number}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          bgcolor: '#ffffff',
                          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>
                          Opportunity #{opportunity.opportunity_number}
                        </Typography>
                        <Typography sx={{ color: '#0f172a', fontWeight: 900, mt: 0.25 }}>
                          {opportunity.role || 'Opportunity'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569', mt: 0.25 }}>
                          {opportunity.company || 'Company not disclosed'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            mt: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {previewText(opportunity.comments)}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            endIcon={<LaunchIcon />}
                            onClick={() => goToOpportunity(opportunity.opportunity_number)}
                            sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#115e59' }, fontWeight: 900 }}
                          >
                            Apply
                          </Button>
                          <Chip size="small" label={`${opportunity.noapplications || 0} applications`} sx={{ bgcolor: '#f8fafc', fontWeight: 800 }} />
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ height: 430, width: '100%' }}>
                    <DataGrid
                      rows={opportunities}
                      columns={columns}
                      pageSizeOptions={[5, 10, 15]}
                      paginationModel={{ pageSize, page }}
                      onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                      }}
                      disableRowSelectionOnClick
                      sx={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 3,
                        bgcolor: '#ffffff',
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: '#f8fafc',
                          color: '#0f172a',
                          borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 900 },
                        '& .MuiDataGrid-row:hover': { bgcolor: '#f0fdfa' },
                      }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default IndustryDetails;
